import { useState } from 'react';

import filterAttibutes from 'data/framework-filters.json';

interface Filter {
  attribute: string | null;
  value: string | null;
}

interface Attribute {
  id: string;
  name: string;
  attributes: string[];
}

export interface FrameworkFilters<
  T extends { filterAttributes: { [key: string]: string[] } } = {
    filterAttributes: { [key: string]: string[] };
  }
> {
  frameworks: T[];
  attributes: Attribute[];
  filters: Filter[];
  setAttribute(index: number, attribute: string | null): void;
  setValue(index: number, value: string | null): void;
  remove(index: number): void;
  reset(): void;
}

export const useFrameworkFilters = <
  T extends { filterAttributes: { [key: string]: string[] } } = {
    filterAttributes: { [key: string]: string[] };
  }
>(
  frameworks: T[]
): FrameworkFilters<T> => {
  const [filters, setFilters] = useState<Filter[]>([]);

  const filteredFrameworks = frameworks.filter((framework) => {
    const result: boolean = filters.reduce((accumulator, filter) => {
      return (
        accumulator &&
        (filter.attribute == null ||
          filter.value == null ||
          framework.filterAttributes[filter.attribute]?.includes(filter.value))
      );
    }, true as boolean);

    return result;
  });

  const setAttribute = (index: number, attribute: string | null) => {
    if (attribute === filters[index]?.attribute) {
      return;
    }

    const newFilters = [...filters];
    newFilters[index] = {
      attribute,
      value: null
    };
    setFilters(newFilters);
  };

  const setValue = (index: number, value: string | null) => {
    const newFilters = [...filters];
    newFilters[index] = {
      attribute: newFilters[index].attribute,
      value
    };
    setFilters(newFilters);
  };

  const remove = (index: number) => {
    const newFilters = [...filters];
    newFilters.splice(index, 1);
    setFilters(newFilters);
  };

  const reset = () => {
    setFilters([]);
  };

  return {
    frameworks: filteredFrameworks,
    attributes: filterAttibutes,
    filters,
    setAttribute,
    setValue,
    remove,
    reset
  };
};
